import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { parsePresetDuration, presetDurationInput, formatPresetDuration } from "../../utils/presetDuration";
import { limits } from "../../Constants/constants";

const EditPreset = (props) => {
  const [presetName, setPresetName] = useState(
    props.editingPreset?.presetName || ""
  );
  const [tasksList, setTasksList] = useState(
    props.editingPreset?.presetTasks || []
  );
  const [isDailyRoutine, setIsDailyRoutine] = useState(props.editingPreset?.isDailyRoutine === true);
  const [taskName, setTaskName] = useState("");
  const [taskTime, setTaskTime] = useState("");
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [editTaskName, setEditTaskName] = useState("");
  const [editTaskTime, setEditTaskTime] = useState("");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const totalMinutes = tasksList.reduce(
    (total, task) => total + Number(task.estimatedTaskTime || 0),
    0
  );

  const handleAddTask = () => {
    if (!taskName.trim() || !Number.isFinite(parsePresetDuration(taskTime, limits.maxTimeLimitPreset))) {
      alert("Please enter valid task details.");
      return;
    }
    if (tasksList.length >= limits.maxTasksPerPreset) {
      alert(
        "You can only add up to " +
          limits.maxTasksPerPreset +
          " tasks per bucket."
      );
      return;
    }

    setTasksList([
      ...tasksList,
      {
        taskName: taskName.trim(),
        estimatedTaskTime: parsePresetDuration(taskTime, limits.maxTimeLimitPreset),
      },
    ]);
    setTaskName("");
    setTaskTime("");
  };

  const handleDeleteTask = (index) => {
    setTasksList(tasksList.filter((_, taskIndex) => taskIndex !== index));
  };

  const handleEditTaskClick = (index) => {
    setEditingTaskIndex(index);
    setEditTaskName(tasksList[index].taskName);
    setEditTaskTime(presetDurationInput(tasksList[index].estimatedTaskTime));
    setOpenEditDialog(true);
  };

  const closeEditDialog = () => {
    setOpenEditDialog(false);
    setEditingTaskIndex(null);
    setEditTaskName("");
    setEditTaskTime("");
  };

  const handleSaveEditTask = () => {
    if (!editTaskName.trim() || !Number.isFinite(parsePresetDuration(editTaskTime, limits.maxTimeLimitPreset))) {
      alert("Please enter valid task details.");
      return;
    }

    const updatedTasks = [...tasksList];
    updatedTasks[editingTaskIndex] = {
      taskName: editTaskName.trim(),
      estimatedTaskTime: parsePresetDuration(editTaskTime, limits.maxTimeLimitPreset),
    };
    setTasksList(updatedTasks);
    closeEditDialog();
  };

  const handleCollectFromMainList = () => {
    const mainTasks = props.mainTasks || [];
    if (mainTasks.length === 0) {
      alert("No tasks available in the main list to collect.");
      return;
    }

    const remainingSlots = limits.maxTasksPerPreset - tasksList.length;
    if (remainingSlots <= 0) {
      alert("Bucket already has maximum allowed tasks.");
      return;
    }

    const convertedTasks = mainTasks.slice(0, remainingSlots).map((task) => ({
      taskName: task.taskName,
      estimatedTaskTime: Math.max(1, Math.round(task.estimatedTaskTime || 0)) / 60,
    }));
    setTasksList([...tasksList, ...convertedTasks]);
  };

  const handleSavePreset = () => {
    if (!presetName.trim()) {
      alert("Preset name is required.");
      return;
    }
    if (tasksList.length < 1) {
      alert("Add Minimum 1 Task in Bucket.");
      return;
    }

    props.updatePreset(
      props.editingPreset.id,
      presetName.trim(),
      tasksList,
      isDailyRoutine
    );
  };

  const handleDrop = (dropIndex) => {
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const reorderedTasks = [...tasksList];
    const [draggedTask] = reorderedTasks.splice(draggedIndex, 1);
    reorderedTasks.splice(dropIndex, 0, draggedTask);
    setTasksList(reorderedTasks);
    setDraggedIndex(null);
  };

  return (
    <section className="edit-preset">
      <header className="edit-preset-header">
        <button
          type="button"
          className="edit-preset-back"
          onClick={props.cancelEdit}
          aria-label="Back to my presets"
        >
          <ArrowBackRoundedIcon />
        </button>
        <div>
          <h2>Edit preset</h2>
          <p>Update the routine name, tasks, and order.</p>
        </div>
        <span className="edit-preset-count">{tasksList.length} tasks</span>
      </header>

      <div className="edit-preset-section edit-preset-name-panel">
        <label className="edit-preset-label" htmlFor="edit-preset-name">
          Preset name
        </label>
        <input
          id="edit-preset-name"
          className="edit-preset-input"
          value={presetName}
          onChange={(event) => setPresetName(event.target.value)}
          maxLength={limits.maxBucketNameLimit}
          placeholder="Name this preset"
        />
        <label className="preset-checkbox">
          <input
            type="checkbox"
            checked={isDailyRoutine}
            onChange={(event) => setIsDailyRoutine(event.target.checked)}
          />
          <span>Daily routine</span>
        </label>
      </div>

      <div className="edit-preset-tasks">
        <div className="edit-preset-tasks-header">
          <div>
            <h3>Tasks in this preset</h3>
            <p>Review, edit, or drag tasks to reorder them.</p>
          </div>
          <div className="edit-preset-list-tools">
            <span>
              {tasksList.length} tasks · {formatPresetDuration(totalMinutes)}
            </span>
            <button
              type="button"
              className="edit-preset-collect"
              onClick={handleCollectFromMainList}
            >
              <PlaylistAddRoundedIcon />
              <span>Import Current Tasks</span>
            </button>
          </div>
        </div>

        <div className="edit-preset-task-list">
          {tasksList.length === 0 ? (
            <div className="edit-preset-empty">
              Add at least one task to save this preset.
            </div>
          ) : (
            tasksList.map((task, index) => (
              <div
                key={`${task.taskName}-${index}`}
                className={`edit-preset-task-row ${
                  draggedIndex === index ? "is-dragging" : ""
                }`}
                draggable
                onDragStart={() => setDraggedIndex(index)}
                onDragEnd={() => setDraggedIndex(null)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleDrop(index)}
              >
                <DragIndicatorRoundedIcon className="edit-preset-drag-icon" />
                <span className="edit-preset-task-number">{index + 1}</span>
                <span className="edit-preset-task-name">{task.taskName}</span>
                <span className="edit-preset-task-duration">
                  {formatPresetDuration(task.estimatedTaskTime)}
                </span>
                <button
                  type="button"
                  className="edit-preset-task-action"
                  onClick={() => handleEditTaskClick(index)}
                  aria-label={`Edit ${task.taskName}`}
                >
                  <EditRoundedIcon />
                </button>
                <button
                  type="button"
                  className="edit-preset-task-action edit-preset-task-delete"
                  onClick={() => handleDeleteTask(index)}
                  aria-label={`Delete ${task.taskName}`}
                >
                  <DeleteOutlineRoundedIcon />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="edit-preset-section edit-preset-composer-section">
        <div className="edit-preset-section-heading">
          <div>
            <h3>Add a task</h3>
                <p>Time: 0.1 = 10 sec, 0.05 = 5 sec, 1.05 = 1 min 5 sec.</p>
            <p>Add another task to this preset.</p>
          </div>
        </div>

        <div className="edit-preset-composer">
          <input
            className="edit-preset-input edit-preset-task-input"
            value={taskName}
            onChange={(event) =>
              setTaskName(
                event.target.value.slice(0, limits.maxInputCharacters)
              )
            }
            placeholder="Task name"
            maxLength={limits.maxInputCharacters}
          />
          <div className="edit-preset-time-field">
            <AccessTimeRoundedIcon />
            <input
              value={taskTime}
              onChange={(event) => setTaskTime(event.target.value)}
              type="number"
              min="0.01"
                    step="0.01"
              max={limits.maxTimeLimitPreset}
              placeholder="Min.sec"
              aria-label="Task duration in minutes and seconds"
            />
          </div>
          <button
            type="button"
            className="edit-preset-add"
            onClick={handleAddTask}
            aria-label="Add task"
          >
            <AddRoundedIcon />
            <span>Add</span>
          </button>
        </div>
      </div>

      <footer className="edit-preset-actions">
        <button
          type="button"
          className="edit-preset-cancel"
          onClick={props.cancelEdit}
        >
          Cancel
        </button>
        <button
          type="button"
          className="edit-preset-save"
          onClick={handleSavePreset}
        >
          <SaveRoundedIcon />
          <span>Save changes</span>
        </button>
      </footer>

      <Dialog
        open={openEditDialog}
        onClose={closeEditDialog}
        className="edit-task-dialog"
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit task</DialogTitle>
        <DialogContent>
          <p className="edit-task-dialog-copy">
            Update this task without leaving your preset.
          </p>
          <div className="edit-task-dialog-fields">
            <TextField
              fullWidth
              size="small"
              label="Task name"
              value={editTaskName}
              onChange={(event) =>
                setEditTaskName(
                  event.target.value.slice(0, limits.maxInputCharacters)
                )
              }
              inputProps={{ maxLength: limits.maxInputCharacters }}
            />
            <TextField
              fullWidth
              size="small"
              label="Duration (min.sec)"
              value={editTaskTime}
              onChange={(event) => setEditTaskTime(event.target.value)}
              type="number"
              helperText="0.1 = 10 sec, 0.05 = 5 sec, 1.05 = 1 min 5 sec"
              inputProps={{ min: 0.01, step: 0.01, max: limits.maxTimeLimitPreset }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="edit-preset-cancel"
            onClick={closeEditDialog}
          >
            Cancel
          </button>
          <button
            type="button"
            className="edit-preset-save"
            onClick={handleSaveEditTask}
          >
            Save task
          </button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default EditPreset;
